import {
  type Category,
  dec,
  type HopForm,
  isGrownForm,
  packOfWholeAmounts,
  toCanonical,
  ZERO,
} from "@bl/domain";
import type { Item, Location, PackOption, StockImport, StockImportCommit } from "../api";
import { clearLocal, readLocal } from "../autosave";
import { type PackChoice, packChoiceOf } from "../forms/PackPicker";

/**
 * Bringing in Ollie's stock (Scott, via Tyler 2026-10-01): choose Ollie's Inventory On Hand Report in
 * Settings, say what each of its items is here, then bring its lots in as opening stock, one entry that
 * Undo takes back out. The review saves itself as it goes, as brew day does, so a long one can be left
 * and finished later.
 */

export type Group = StockImport["groups"][number];
export type Decision = StockImportCommit["groups"][number]["decision"];
/**
 * What the brewer has said a group is. A name nothing answers word for word starts undecided, and the
 * import waits until every one is said (Tyler, 2026-10-19): left to become a new item on its own, Ollie's
 * "Thomas Fawcett Torrified Wheat" became an item beside the catalog's Torrefied Wheat, and CMG's
 * invoice put the bag in the other one.
 */
type Choice = Decision | { kind: "undecided" };
export type Candidate = Group["suggestion"]["candidates"][number];
export type ReportType = Group["type"];
export interface GroupChoice {
  decision: Choice;
  /** A catalog product found by searching, for saying which it is and what packs it comes in. */
  picked?: {
    id: string;
    name: string;
    category?: Category;
    /** Its variety and form, for finding the item a hop as grown goes into. */
    hop?: { key: string | null; form: HopForm } | null;
  };
  /**
   * What its lots came in. Left unset, the page's guess (`guessPack`); null, the brewer said none.
   * Ollie's report says only weight (Tyler, 2026-10-02).
   */
  pack?: PackChoice;
  /**
   * By lot index: whether it comes in, and where if not where its kind goes. Hops also say where they
   * were grown and the farm, where the brewer knows: Ollie's report says neither.
   */
  lots: Record<
    string,
    { include: boolean; locationId: string | null; region?: string; grower?: string }
  >;
}
export interface ReviewState {
  v: 1;
  /** Where each kind of stock goes unless a lot says otherwise. */
  typeLocations: Partial<Record<ReportType, string>>;
  groups: Record<string, GroupChoice>;
}
/** What a group goes in as before anyone says: what was suggested, else nothing yet. */
export function defaultDecision(g: Group): Choice {
  if (g.suggestion.item) return { kind: "item", itemId: g.suggestion.item.id };
  if (g.suggestion.catalogProduct)
    return { kind: "catalog", productId: g.suggestion.catalogProduct.id };
  return { kind: "undecided" };
}
/** A product the page offered for a name, as what was picked: what it is and which hop. */
export const pickedOf = (c: Candidate): NonNullable<GroupChoice["picked"]> => ({
  id: c.id,
  name: c.name,
  category: c.category as Category,
  hop: c.hop,
});
/**
 * The inventory item a choice puts the lots into, if it is one already here: the item chosen, or the
 * item already made from the catalog product chosen. A hop as grown goes into its variety and form's
 * item, from whichever product it was made, as the import does (Scott, 2026-10-02). `picked` is a
 * product found by searching, which says which hop it is.
 */
export function itemFor(
  g: Group,
  decision: Choice,
  items: Item[],
  picked?: GroupChoice["picked"],
): Item | undefined {
  if (decision.kind === "item") return items.find((i) => i.id === decision.itemId);
  if (decision.kind !== "catalog") return undefined;
  const made = items.find((i) => !i.archived && i.catalogProductId === decision.productId);
  if (made) return made;
  const hop =
    g.suggestion.catalogProduct?.id === decision.productId
      ? g.suggestion.catalogProduct.hop
      : picked?.id === decision.productId
        ? picked.hop
        : null;
  return hop?.key && isGrownForm(hop.form)
    ? items.find((i) => !i.archived && i.hopVariety === hop.key && i.hopForm === hop.form)
    : undefined;
}
/** Lots of an item that already has stock start unticked, so nothing is counted twice. */
export function lotsFor(
  g: Group,
  decision: Choice,
  items: Item[],
  picked?: GroupChoice["picked"],
): GroupChoice["lots"] {
  const into = itemFor(g, decision, items, picked);
  const has = !!into && dec(into.onHand.quantity).gt(ZERO);
  return Object.fromEntries(
    g.lots.map((l) => [String(l.index), { include: !has, locationId: null }]),
  );
}
/** What an item made or found by this choice is, for the packs it can come in. */
export function categoryFor(g: Group, choice: GroupChoice, items: Item[]): Category | null {
  const d = choice.decision;
  if (d.kind === "item")
    return (items.find((i) => i.id === d.itemId)?.category as Category) ?? null;
  if (d.kind === "catalog") {
    const into = itemFor(g, d, items, choice.picked);
    if (into) return into.category as Category;
    if (d.productId === g.suggestion.catalogProduct?.id) {
      return g.suggestion.catalogProduct.category as Category;
    }
    return choice.picked?.category ?? null;
  }
  return d.kind === "new" ? d.category : null;
}
export const packKey = (g: Group, category: Category | null) => `${g.dimension}|${category ?? ""}`;
export type PackGuess = { pack: PackChoice; why: "item" | "only" | "whole" | null };
/**
 * What a group's lots probably came in, since Ollie says only weight (Tyler, 2026-10-02): the pack the
 * item they go into already has; else the only pack its kind comes in; else the one pack every lot is
 * a whole number of (100 lb is two 50 lb bags and no number of 55 lb ones). Otherwise none, and the
 * lots are shown in the item's pack once an invoice gives it one.
 */
export function guessPack(g: Group, into: Item | undefined, options: PackOption[]): PackGuess {
  if (into?.pack) return { pack: packChoiceOf(into.pack, g.dimension), why: "item" };
  const only = options.length === 1 ? options[0] : undefined;
  if (only) return { pack: packChoiceOf(only, g.dimension), why: "only" };
  const amounts = g.lots.map((l) => toCanonical({ value: l.quantity, unit: l.unit }, g.dimension));
  const whole = packOfWholeAmounts(amounts, options);
  if (whole) return { pack: packChoiceOf(whole, g.dimension), why: "whole" };
  return { pack: null, why: null };
}
function defaultPlace(locations: Location[], words: string[]): string | undefined {
  return (
    locations.find((l) => words.every((w) => l.name.toLowerCase().includes(w)))?.id ??
    locations[0]?.id
  );
}
function defaultState(report: StockImport, items: Item[], locations: Location[]): ReviewState {
  return {
    v: 1,
    typeLocations: {
      Fermentable: defaultPlace(locations, ["grain"]),
      Hop: defaultPlace(locations, ["hop"]),
      Miscellaneous: defaultPlace(locations, ["dry"]),
      "Packaging Material": defaultPlace(locations, ["dry"]),
    },
    groups: Object.fromEntries(
      report.groups.map((g) => {
        const decision = defaultDecision(g);
        return [g.key, { decision, lots: lotsFor(g, decision, items) }];
      }),
    ),
  };
}
export const localKeyOf = (id: string) => `bl.stock-import.${id}`;
/** Where the page starts: this device's unsent copy if newer than the server's, else the server's. */
export function startingState(report: StockImport, items: Item[], locations: Location[]) {
  const local = readLocal<ReviewState>(localKeyOf(report.id));
  const server = report.state as ReviewState | null;
  if (
    local &&
    (!report.stateSavedAt || Date.parse(local.savedAt) > Date.parse(report.stateSavedAt))
  ) {
    return { state: local.value, unsent: true };
  }
  if (local) clearLocal(localKeyOf(report.id));
  if (server?.v === 1) return { state: server, unsent: false };
  return { state: defaultState(report, items, locations), unsent: false };
}
